import { Archive, Flame, Package, Refrigerator, Snowflake, Store } from "lucide-react";

/**
 * Regras de exibição e validação de locais de armazenamento.
 * API (StorageLocationDTO): { id: string, name, description, equipment_id: string,
 * equipment_name, capacity: number, used_capacity: number }
 * Banco: name entre 3 e 100 caracteres; capacity INTEGER (pode ser nulo).
 * used_capacity = soma do saldo dos lotes de estoque guardados no local.
 */

export const NAME_MIN = 3;
export const NAME_MAX = 100;
export const CAPACITY_MAX = 1000000;

/** Ocupação a partir da qual o local é "crítico" / pede "atenção" */
export const CRITICAL_USAGE = 90;
export const WARNING_USAGE = 70;

/** Minúsculas e sem acento, para busca e comparação */
export const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const toInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

export const getCapacity = (location) => Math.max(toInt(location?.capacity), 0);
export const getUsed = (location) => Math.max(toInt(location?.used_capacity), 0);

/** Percentual de ocupação (pode passar de 100); null quando não há capacidade */
export const getUsage = (capacity, used) =>
  capacity > 0 ? Math.round((used / capacity) * 100) : null;

export const STATUS = {
  critical: {
    label: "Crítico",
    badge: "bg-destructive/10 text-destructive",
    dot: "bg-destructive",
    bar: "bg-destructive",
    text: "text-destructive",
  },
  warning: {
    label: "Atenção",
    badge: "bg-terra-soft text-terra",
    dot: "bg-terra",
    bar: "bg-terra",
    text: "text-terra",
  },
  normal: {
    label: "Disponível",
    badge: "bg-secondary text-secondary-foreground",
    dot: "bg-primary",
    bar: "bg-primary",
    text: "text-primary",
  },
  unset: {
    label: "Sem capacidade",
    badge: "bg-muted text-muted-foreground",
    dot: "bg-muted-foreground",
    bar: "bg-muted-foreground",
    text: "text-muted-foreground",
  },
};

export const getStatusKey = (usage) => {
  if (usage === null) return "unset";
  if (usage >= CRITICAL_USAGE) return "critical";
  if (usage >= WARNING_USAGE) return "warning";
  return "normal";
};

/** Dados prontos para a tela: capacidade, uso, saldo, % e status */
export const getOccupancy = (location) => {
  const capacity = getCapacity(location);
  const used = getUsed(location);
  const usage = getUsage(capacity, used);
  const statusKey = getStatusKey(usage);
  return { capacity, used, available: capacity - used, usage, statusKey, status: STATUS[statusKey] };
};

/** 1234 → "1.234" */
export const formatUnits = (n) => Number(n || 0).toLocaleString("pt-BR");

// Ícone do equipamento pelo nome ("Freezer Principal" → floco de neve)
const EQUIPMENT_ICONS = [
  [Snowflake, "freezer congelador freezers"],
  [Refrigerator, "geladeira refrigerador camara fria frios resfriador"],
  [Flame, "estufa forno aquecedor"],
  [Store, "vitrine balcao expositor"],
  [Archive, "prateleira estante despensa armario gaveta deposito"],
].map(([icon, keywords]) => [icon, new Set(keywords.split(" "))]);

export const getEquipmentIcon = (name) => {
  const words = normalize(name).split(/[^a-z0-9]+/).filter(Boolean);
  for (const word of words) {
    const hit = EQUIPMENT_ICONS.find(([, keywords]) => keywords.has(word));
    if (hit) return hit[0];
  }
  return Package;
};

/**
 * Valida o formulário. `others` = demais locais (mesmo nome no mesmo
 * equipamento confunde a escolha do local no estoque).
 * `description` não tem campo na tela: é repassada para o PUT não apagá-la.
 * Retorna { errors, payload } — payload só quando não há erros.
 */
export const validateStorageLocation = ({ name, equipment_id, capacity, description }, others = []) => {
  const errors = {};
  const cleanName = String(name ?? "").trim().replace(/\s+/g, " ");
  const equipmentId = String(equipment_id ?? "").trim();
  const capacityText = String(capacity ?? "").trim();
  const capacityValue = Number(capacityText);

  if (!cleanName) errors.name = "Informe o nome do local";
  else if (cleanName.length < NAME_MIN) errors.name = `Use ao menos ${NAME_MIN} caracteres`;
  else if (cleanName.length > NAME_MAX) errors.name = `Máximo de ${NAME_MAX} caracteres`;
  else if (
    equipmentId &&
    others.some(
      (l) => normalize(l.name) === normalize(cleanName) && String(l.equipment_id ?? "") === equipmentId
    )
  )
    errors.name = "Já existe um local com esse nome neste equipamento";

  if (!equipmentId) errors.equipment_id = "Selecione o equipamento";

  if (!capacityText) errors.capacity = "Informe a capacidade";
  else if (!/^\d+$/.test(capacityText)) errors.capacity = "Use um número inteiro de unidades";
  else if (capacityValue <= 0) errors.capacity = "A capacidade deve ser maior que zero";
  else if (capacityValue > CAPACITY_MAX) errors.capacity = `Máximo de ${formatUnits(CAPACITY_MAX)} unidades`;

  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    payload: {
      name: cleanName,
      description: description ?? null,
      equipment_id: equipmentId,
      capacity: capacityValue,
    },
  };
};
